<script setup lang="ts">
import BaseModal from './BaseModal.vue'

interface Props {
  open?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  open: false
})

const emit = defineEmits<{
  close: []
  accept: []
}>()

const handleAccept = () => {
  emit('accept')
  emit('close')
}
</script>

<template>
  <BaseModal :open="open" max-width="600px" @close="$emit('close')">
    <template #header>
      <h2 class="terms-modal__title">Terms of Service</h2>
    </template>

    <div class="terms-modal__content">
      <p class="terms-modal__date">Last Updated: November 20, 2025</p>

      <section class="terms-modal__section">
        <h3>1. Acceptance of Terms</h3>
        <p>
          By using MyCryptoBox (the "Service"), provided by MyCryptoBox LLC ("Company", "we", "us", or "our"),
          you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Service.
        </p>
      </section>

      <section class="terms-modal__section">
        <h3>2. Non-Custodial Service</h3>
        <p>
          <strong>THE SERVICE IS NON-CUSTODIAL.</strong> You are solely responsible for:
        </p>
        <ul>
          <li>The security and confidentiality of your wallet mnemonic phrases, private keys, and PIN codes</li>
          <li>All transactions initiated from your wallet</li>
          <li>The safekeeping of your cryptocurrency assets</li>
        </ul>
        <p>
          <strong>WE DO NOT HAVE ACCESS TO YOUR FUNDS.</strong> We cannot recover lost PINs, forgotten mnemonic phrases,
          or retrieve funds sent to incorrect addresses. There is no password reset or account recovery mechanism.
        </p>
      </section>

      <section class="terms-modal__section">
        <h3>3. No Liability</h3>
        <p>
          <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY SHALL NOT BE LIABLE FOR:</strong>
        </p>
        <ul>
          <li>Loss of cryptocurrency due to forgotten PINs or lost mnemonic phrases</li>
          <li>Unauthorized access to your wallet resulting from your failure to maintain security</li>
          <li>Failed transactions, network delays, or blockchain errors</li>
          <li>Loss of funds due to smart contract bugs, protocol failures, or network attacks</li>
          <li>Changes in cryptocurrency value or market conditions</li>
          <li>Any direct, indirect, incidental, consequential, or punitive damages</li>
          <li>Technical issues, downtime, or service interruptions</li>
        </ul>
      </section>

      <section class="terms-modal__section">
        <h3>4. Service Provided "AS-IS"</h3>
        <p>
          The Service is provided on an "AS-IS" and "AS-AVAILABLE" basis without any warranties of any kind,
          whether express or implied, including but not limited to:
        </p>
        <ul>
          <li>Warranties of merchantability or fitness for a particular purpose</li>
          <li>Warranties regarding security, accuracy, or reliability</li>
          <li>Warranties that the Service will be uninterrupted or error-free</li>
        </ul>
      </section>

      <section class="terms-modal__section">
        <h3>5. User Responsibilities</h3>
        <p>You acknowledge and agree that you are solely responsible for:</p>
        <ul>
          <li>Verifying all transaction details before confirming</li>
          <li>Understanding the risks associated with cryptocurrency</li>
          <li>Complying with all applicable laws and regulations in your jurisdiction</li>
          <li>Paying any applicable taxes on your cryptocurrency transactions</li>
          <li>Securing your devices, internet connection, and wallet credentials</li>
        </ul>
      </section>

      <section class="terms-modal__section">
        <h3>6. Assumption of Risk</h3>
        <p>
          <strong>YOU EXPRESSLY ACKNOWLEDGE AND ASSUME ALL RISKS</strong> associated with using cryptocurrency, including:
        </p>
        <ul>
          <li>High volatility and potential total loss of value</li>
          <li>Irreversibility of transactions</li>
          <li>Regulatory uncertainty and potential legal changes</li>
          <li>Technical vulnerabilities in blockchain protocols</li>
          <li>Risks of hacking, phishing, and social engineering attacks</li>
        </ul>
      </section>

      <section class="terms-modal__section">
        <h3>7. No Financial Advice</h3>
        <p>
          The Service does not provide financial, investment, legal, or tax advice. You should consult with
          qualified professionals before making any financial decisions.
        </p>
      </section>

      <section class="terms-modal__section">
        <h3>8. Prohibited Uses</h3>
        <p>You agree not to use the Service for:</p>
        <ul>
          <li>Any illegal activities or purposes</li>
          <li>Money laundering, terrorist financing, or other criminal activities</li>
          <li>Violating any applicable laws, regulations, or third-party rights</li>
          <li>Interfering with the proper functioning of the Service</li>
        </ul>
      </section>

      <section class="terms-modal__section">
        <h3>9. Service Modifications and Termination</h3>
        <p>
          We reserve the right to modify, suspend, or discontinue the Service at any time without notice or liability.
          We may also update these Terms at any time. Your continued use of the Service constitutes acceptance of any changes.
        </p>
      </section>

      <section class="terms-modal__section">
        <h3>10. Limitation of Liability</h3>
        <p>
          <strong>IN NO EVENT SHALL THE COMPANY, ITS DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY DAMAGES WHATSOEVER,
          INCLUDING BUT NOT LIMITED TO DIRECT, INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES, ARISING OUT OF
          OR IN CONNECTION WITH YOUR USE OF THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</strong>
        </p>
        <p>
          Some jurisdictions do not allow the exclusion of certain warranties or limitations of liability.
          In such jurisdictions, our liability shall be limited to the maximum extent permitted by law.
        </p>
      </section>

      <section class="terms-modal__section">
        <h3>11. Indemnification</h3>
        <p>
          You agree to indemnify, defend, and hold harmless MyCryptoBox LLC and its affiliates, directors, officers,
          employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees)
          arising from your use of the Service or violation of these Terms.
        </p>
      </section>

      <section class="terms-modal__section">
        <h3>12. Governing Law</h3>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the United States,
          without regard to its conflict of law provisions.
        </p>
      </section>

      <section class="terms-modal__section">
        <h3>13. Contact Information</h3>
        <p>
          For questions about these Terms, please contact us at: <a href="mailto:support@mycryptobox.com">support@mycryptobox.com</a>
        </p>
      </section>

      <div class="terms-modal__warning">
        <strong>⚠️ IMPORTANT REMINDER:</strong> This is a non-custodial wallet service. We cannot recover your funds
        if you lose your mnemonic phrase or PIN. Store your recovery information securely and never share it with anyone.
      </div>
    </div>

    <template #footer>
      <div class="terms-modal__footer">
        <button type="button" class="btn-secondary" @click="$emit('close')">
          Close
        </button>
        <button type="button" class="btn-primary" @click="handleAccept">
          Accept Terms
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.terms-modal__title {
  font-size: 24px;
  font-weight: 700;
  color: #182855;
  margin: 0;
}

.terms-modal__date {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 20px 0;
}

.terms-modal__content {
  font-size: 14px;
  line-height: 1.6;
  color: #334155;
}

.terms-modal__section {
  margin-bottom: 24px;
}

.terms-modal__section:last-of-type {
  margin-bottom: 0;
}

.terms-modal__section h3 {
  font-size: 16px;
  font-weight: 600;
  color: #182855;
  margin: 0 0 8px 0;
}

.terms-modal__section p {
  margin: 0 0 12px 0;
}

.terms-modal__section p:last-child {
  margin-bottom: 0;
}

.terms-modal__section ul {
  margin: 8px 0;
  padding-left: 24px;
}

.terms-modal__section li {
  margin-bottom: 6px;
}

.terms-modal__section li:last-child {
  margin-bottom: 0;
}

.terms-modal__section strong {
  color: #182855;
  font-weight: 600;
}

.terms-modal__section a {
  color: #8b5cf6;
  text-decoration: underline;
}

.terms-modal__section a:hover {
  color: #7c3aed;
}

.terms-modal__warning {
  margin-top: 24px;
  padding: 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #78350f;
}

.terms-modal__warning strong {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
}

.terms-modal__footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.terms-modal__footer button {
  min-width: 100px;
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .terms-modal__title {
    font-size: 20px;
  }

  .terms-modal__section h3 {
    font-size: 15px;
  }

  .terms-modal__content {
    font-size: 13px;
  }

  .terms-modal__footer {
    flex-direction: column-reverse;
  }

  .terms-modal__footer button {
    width: 100%;
  }
}
</style>
